import groupBy from "lodash/groupBy";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import transform from "lodash/transform";
import React from "react";
import { Detail } from "react-calendar";
import {
  AccountType,
  CalendarDate,
  CheckboxGroup,
  DatePickerType,
  DatePickerUniversal,
  DateValidity,
  DropdownCheckbox,
  ErrorMessageLocation,
  FilterControl,
  FilterDrawer,
  ICheckboxGroupValue,
  IDropdownCheckboxProps,
  IDropdownCheckboxSearchInput,
  IFilterTagProps,
  IOption,
} from "component-library";
import {
  withLaunchDarklyConsumerHOC,
  LaunchDarklyClientWithFlags,
  FEATURE_FLAGS,
} from "shared-services";
import { mapCheckboxValueToList } from "shared/helpers/mappers";
import { ValueOf } from "shared/types/utilities";

import {
  mapCheckboxGroupValueToValues,
  mapOptionsToCheckboxGroupValue,
} from "helpers/mappers";

import {
  ChangeFilters,
  IWithSelfTapeRequestNotificationsFilterTexts,
} from "hoc/withSelfTapeRequestNotificationsFilter/withSelfTapeRequestNotificationsFilter";
import {
  FilterControlType,
  FilterType,
  IFilterConfig,
  IFilterConfigItem,
  IFilterModel,
} from "hoc/withSelfTapeRequestNotificationsFilter/withSelfTapeRequestNotificationsFilter.config";

export type ISelfTapeRequestNotificationFilterControlModel = Pick<
  IFilterModel,
  FilterType.Client | FilterType.DateReceived
>;

export interface ISelfTapeRequestNotificationFilterTag {
  tagProps: IFilterTagProps;
  filterType: FilterType;
  value: CalendarDate | number | string;
}

interface ISelfTapeRequestNotificationFilterControlState {
  filterDrawerValues: Partial<ISelfTapeRequestNotificationFilterControlModel>;
  isFilterDrawerShown: boolean;
  tags: ISelfTapeRequestNotificationFilterTag[];
  isViewTagsPopupShown: boolean;
}

export interface ISelfTapeRequestNotificationFilterControlComposedProps {
  locale: string;
  placeholders: IWithSelfTapeRequestNotificationsFilterTexts;
  changeFilters: ChangeFilters;
  filterValues: IFilterModel;
  filterConfig: IFilterConfig;
  accountType: AccountType;
}

export interface ISelfTapeRequestNotificationFilterControlProps
  extends ISelfTapeRequestNotificationFilterControlComposedProps,
    LaunchDarklyClientWithFlags {}

export class SelfTapeRequestNotificationFilterControl extends React.Component<
  ISelfTapeRequestNotificationFilterControlProps,
  ISelfTapeRequestNotificationFilterControlState
> {
  constructor(props: ISelfTapeRequestNotificationFilterControlProps) {
    super(props);

    const initialValues = this.getInitialValues();

    this.state = {
      filterDrawerValues: initialValues,
      isFilterDrawerShown: false,
      tags: [],
      isViewTagsPopupShown: false,
    };
  }

  public componentDidMount() {
    this.refreshValuesAndTags();
  }

  public componentDidUpdate(
    prevProps: ISelfTapeRequestNotificationFilterControlProps
  ) {
    if (!isEqual(this.props.filterConfig, prevProps.filterConfig)) {
      this.generateTags();
    }

    if (!isEqual(this.props.filterValues, prevProps.filterValues)) {
      this.refreshValuesAndTags();
    }
  }

  public render() {
    const filterControlTags = this.state.tags.map((tag) => tag.tagProps);

    return (
      <div className="g-bg-secondary">
        <FilterControl
          onViewAllFilters={this.openFilterDrawer}
          onClearAllFilters={this.handleClearAllFilters}
          locale={this.props.locale}
          tags={filterControlTags}
          items={this.getControlItems()}
          onApplyTags={this.handleApplyTags}
        />
        {this.state.isFilterDrawerShown && (
          <FilterDrawer
            filters={this.getFilterDrawerFilters()}
            locale={this.props.locale}
            onClose={this.closeFilterDrawer}
            onClearAllFilters={this.handleClearAllFilters}
            onApplyFilters={this.applyFilters}
          />
        )}
      </div>
    );
  }

  private getInitialValues = () => {
    const { accountType, filterConfig } = this.props;

    const values = {
      [FilterType.DateReceived]:
        filterConfig[FilterType.DateReceived].defaultValue,
    };

    if (accountType === AccountType.Agent) {
      values[FilterType.Client] = filterConfig[FilterType.Client].defaultValue;
    }

    return values;
  };

  private getControlItems = () => {
    const { accountType, flags } = this.props;
    const isAudioClipRequestEnabled = flags[FEATURE_FLAGS.AudioClipRequest];
    const controls = [];

    if (accountType === AccountType.Agent) {
      controls.push(this.getClientFilter());
    }

    if (isAudioClipRequestEnabled) {
      controls.push(this.getMediaRequestFilter());
    }

    controls.push(this.getDateReceivedFilter());

    return controls;
  };

  private getClientFilter = () => {
    const { filterConfig, placeholders } = this.props;
    const { filterDrawerValues } = this.state;
    const clientTypeConfig = this.getDropdownCheckboxGroupConfig(
      FilterType.Client
    );

    const selectedOptions = {
      ...mapOptionsToCheckboxGroupValue(
        filterConfig[FilterType.Client].checkboxOptions,
        false
      ),
      ...filterDrawerValues[FilterType.Client],
    };

    const searchInputProps: IDropdownCheckboxSearchInput = {
      placeholder: placeholders.clientSearchPlaceholder,
    };

    return (
      <DropdownCheckbox
        key={FilterType.Client}
        searchInput={searchInputProps}
        checkboxGroupConfig={clientTypeConfig}
        value={selectedOptions}
        valueChanged={this.bindCheckboxUpdateHandler(
          FilterType.Client,
          this.applyFilters
        )}
        //@ts-expect-error figure out typing
        ValueComponent={DropdownCheckbox.getDefaultValueComponent({
          selectionPlaceholder: placeholders.clientDropdownPlaceholder,
          selectionPrefix: "",
        })}
      />
    );
  };

  private getMediaRequestFilter = () => {
    const { filterConfig } = this.props;
    const { filterDrawerValues } = this.state;
    const clientTypeConfig = this.getDropdownCheckboxGroupConfig(
      FilterType.MediaRequestType
    );

    const selectedOptions = {
      ...mapOptionsToCheckboxGroupValue(
        filterConfig[FilterType.MediaRequestType].checkboxOptions,
        false
      ),
      ...filterDrawerValues[FilterType.MediaRequestType],
    };

    return (
      <DropdownCheckbox
        key={FilterType.MediaRequestType}
        checkboxGroupConfig={clientTypeConfig}
        value={selectedOptions}
        valueChanged={this.bindCheckboxUpdateHandler(
          FilterType.MediaRequestType,
          this.applyFilters
        )}
        //@ts-expect-error figure out typing
        ValueComponent={DropdownCheckbox.getDefaultValueComponent({
          selectionPlaceholder:
            filterConfig[FilterType.MediaRequestType].translations,
          selectionPrefix: "",
        })}
      />
    );
  };

  private getDateReceivedFilter = () => {
    const { filterValues, placeholders } = this.props;

    return (
      <DatePickerUniversal
        key={FilterType.DateReceived}
        type={DatePickerType.Range}
        allowTextInput={false}
        placeholder={placeholders.dateReceivedInputPlaceholder}
        errorMessageLocation={ErrorMessageLocation.Calendar}
        valueChanged={this.bindDateChangeHandler(
          FilterType.DateReceived,
          this.applyFilters
        )}
        value={filterValues[FilterType.DateReceived]}
        muteExternalValueChange={true}
      />
    );
  };

  private getFilterDrawerFilters = () => {
    const { accountType, flags } = this.props;
    const isAudioClipRequestEnabled = flags[FEATURE_FLAGS.AudioClipRequest];
    const controls = [];

    if (AccountType.Agent === accountType) {
      controls.push(this.getDateReceivedFilterDrawer());
    }

    if (isAudioClipRequestEnabled) {
      controls.push(
        this.getMediaRequestFilterDrawer(),
        this.getClientFilterDrawer()
      );
    }

    return controls;
  };

  private getDateReceivedFilterDrawer = () => {
    const { placeholders, filterConfig } = this.props;
    const { filterDrawerValues } = this.state;

    const productionTypeFilterConfig = filterConfig[FilterType.Client];

    return {
      title: placeholders.clientDropdownPlaceholder,
      id: FilterType.Client,
      selected: !isEmpty(
        mapCheckboxValueToList(filterDrawerValues[FilterType.Client]!)
      ),
      renderFilterContent: () => (
        <CheckboxGroup
          config={{
            name: FilterType.Client,
            options: productionTypeFilterConfig.checkboxOptions!,
          }}
          value={{
            ...mapOptionsToCheckboxGroupValue(
              productionTypeFilterConfig.checkboxOptions,
              false
            ),
            ...filterDrawerValues[FilterType.Client],
          }}
          valueChanged={this.bindCheckboxUpdateHandler(FilterType.Client)}
        />
      ),
    };
  };

  private getMediaRequestFilterDrawer = () => {
    const { filterConfig } = this.props;
    const { filterDrawerValues } = this.state;
    const { checkboxOptions, translations } =
      filterConfig[FilterType.MediaRequestType];

    return {
      title: translations,
      id: FilterType.MediaRequestType,
      selected: !isEmpty(
        mapCheckboxValueToList(filterDrawerValues[FilterType.MediaRequestType])
      ),
      renderFilterContent: () => (
        <CheckboxGroup
          config={{
            name: FilterType.MediaRequestType,
            options: checkboxOptions,
          }}
          value={filterDrawerValues[FilterType.MediaRequestType]}
          valueChanged={this.bindCheckboxUpdateHandler(
            FilterType.MediaRequestType
          )}
        />
      ),
    };
  };

  private getClientFilterDrawer = () => {
    const { placeholders } = this.props;
    const { filterDrawerValues } = this.state;

    return {
      title: placeholders.dateReceivedInputPlaceholder,
      id: FilterType.DateReceived,
      selected: Boolean(filterDrawerValues[FilterType.DateReceived]),
      renderFilterContent: () => (
        <DatePickerUniversal
          key={FilterType.DateReceived}
          type={DatePickerType.Range}
          allowTextInput={false}
          placeholder={placeholders.dateReceivedInputPlaceholder}
          errorMessageLocation={ErrorMessageLocation.Calendar}
          valueChanged={this.bindDateChangeHandler(FilterType.DateReceived)}
          value={filterDrawerValues[FilterType.DateReceived]}
        />
      ),
    };
  };

  private handleClearAllFilters = () => {
    const updatedValues = {} as ISelfTapeRequestNotificationFilterControlModel;
    const { filterConfig, changeFilters } = this.props;

    Object.keys(filterConfig).forEach((filterId: FilterType) => {
      updatedValues[filterId] = filterConfig[filterId].defaultValue;
    });

    changeFilters(updatedValues);
  };

  private handleApplyTags = (tags: IFilterTagProps[]) => {
    const removedTags = this.state.tags.filter((tag) =>
      tags.every((t) => tag.tagProps.id !== t.id)
    );
    this.removeFilterTags(removedTags);
  };

  private bindCheckboxUpdateHandler =
    (filterType: FilterType, callback?: VoidFunction) =>
    (value: ICheckboxGroupValue) => {
      this.setState(
        {
          filterDrawerValues: {
            ...this.state.filterDrawerValues,
            [filterType]: value,
          },
        },
        callback
      );
    };

  private bindDateChangeHandler =
    (filterType: FilterType, callback?: VoidFunction) =>
    (date: Date[], detailsLevel: Detail, validity: DateValidity) => {
      const { filterDrawerValues } = this.state;

      if (
        validity === DateValidity.Valid &&
        (date === undefined || date.length > 1)
      ) {
        this.setState(
          {
            filterDrawerValues: { ...filterDrawerValues, [filterType]: date },
          },
          callback
        );
      }
    };

  private refreshValuesAndTags() {
    this.setState(
      { filterDrawerValues: this.props.filterValues },
      this.generateTags
    );
  }

  private openFilterDrawer = () => {
    this.setState({ isFilterDrawerShown: true });
  };

  private closeFilterDrawer = (updatedValues?: IFilterModel) => {
    this.setState({ isFilterDrawerShown: false }, () => {
      this.setState({
        filterDrawerValues: updatedValues ?? this.props.filterValues,
      });
    });
  };

  private applyFilters = () => {
    const { filterDrawerValues } = this.state;
    const { filterValues } = this.props;

    if (!isEqual(filterDrawerValues, filterValues)) {
      this.props.changeFilters(
        { ...filterDrawerValues, [FilterType.PageNumber]: 1 },
        (updatedValues: IFilterModel) => {
          this.generateTags(updatedValues);
          this.closeFilterDrawer(updatedValues);
        }
      );
    } else {
      this.closeFilterDrawer();
    }
  };

  private generateTags = (updatedValues?: IFilterModel) => {
    const filterValues = updatedValues ?? this.props.filterValues;

    const updatedTags = Object.values(FilterType).reduce(
      (result: ISelfTapeRequestNotificationFilterTag[], filterType) => {
        return this.getUpdatedTags(
          filterType,
          filterValues[filterType],
          result
        );
      },
      []
    );

    this.setState({ tags: updatedTags });
  };

  private getUpdatedTags = (
    filterType: FilterType,
    value: ValueOf<IFilterModel> | IOption<number> | boolean,
    tags: ISelfTapeRequestNotificationFilterTag[] = [],
    useFilterDrawerControlType: boolean = true
  ): ISelfTapeRequestNotificationFilterTag[] => {
    if (!this.props.filterConfig[filterType]) {
      return tags;
    }

    const { filterControlType, drawerFilterControlType } =
      this.props.filterConfig[filterType];
    const controlType = useFilterDrawerControlType
      ? drawerFilterControlType
      : filterControlType;

    if (controlType === FilterControlType.DatePicker) {
      return this.getUpdatedTagsFromDatePicker(
        filterType,
        value as CalendarDate,
        tags
      );
    }

    if (controlType === FilterControlType.Dropdown) {
      return this.getUpdatedTagsFromDropdown(
        filterType,
        value as IOption<number>,
        tags
      );
    }

    if (controlType === FilterControlType.Checkbox && value !== undefined) {
      return this.getUpdatedTagsFromCheckboxGroup(
        filterType,
        value as ICheckboxGroupValue,
        tags
      );
    }

    return tags;
  };

  private getUpdatedTagsFromDatePicker = (
    filterType: FilterType,
    date: CalendarDate,
    tags: ISelfTapeRequestNotificationFilterTag[]
  ) => {
    const tagId = SelfTapeRequestNotificationFilterControl.getTagId(
      filterType,
      filterType
    );
    const updatedTags = tags.filter(({ tagProps }) => tagProps.id !== tagId);

    if (date !== undefined) {
      const stringValue = DatePickerUniversal.getStringValue(date);
      updatedTags.push(
        this.createFilterTag(tagId, filterType, stringValue, date)
      );
    }

    return updatedTags;
  };

  private getUpdatedTagsFromCheckboxGroup = (
    filterType: FilterType,
    value: ICheckboxGroupValue,
    tags: ISelfTapeRequestNotificationFilterTag[]
  ) => {
    const { checkboxOptions = [] } = this.props.filterConfig[
      filterType
    ] as IFilterConfigItem;
    const selectedIds = mapCheckboxGroupValueToValues(value);
    const selectedOptions = checkboxOptions.filter((option: any) =>
      selectedIds.includes(option.value)
    );

    if (!selectedOptions.length) {
      return tags;
    }

    return selectedOptions.reduce(
      (updatedTags: ISelfTapeRequestNotificationFilterTag[], option) => {
        const tagId = SelfTapeRequestNotificationFilterControl.getTagId(
          filterType,
          option.value
        );
        updatedTags = updatedTags.filter(
          ({ tagProps }) => tagProps.id !== tagId
        );

        if (!updatedTags.some(({ tagProps }) => tagProps.id === tagId)) {
          updatedTags.push(
            this.createFilterTag(tagId, filterType, option.label, option.value)
          );
        }

        return updatedTags;
      },
      tags
    );
  };

  private getUpdatedTagsFromDropdown = (
    filterType: FilterType,
    option: IOption<number>,
    tags: ISelfTapeRequestNotificationFilterTag[]
  ) => {
    const tagId = SelfTapeRequestNotificationFilterControl.getTagId(
      filterType,
      String(option.value)
    );
    const updatedTags = tags.filter(({ tagProps }) => tagProps.id !== tagId);

    if (!updatedTags.some(({ tagProps }) => tagProps.id === tagId)) {
      updatedTags.push(
        this.createFilterTag(tagId, filterType, option.name, option.value)
      );
    }

    return updatedTags;
  };

  private createFilterTag(
    tagId: string,
    filterType: FilterType,
    name: string,
    value: CalendarDate | number | string
  ): ISelfTapeRequestNotificationFilterTag {
    const tag: ISelfTapeRequestNotificationFilterTag = {
      tagProps: {
        text: SelfTapeRequestNotificationFilterControl.getTagName(
          this.props.filterConfig[filterType].translations,
          name
        ),
        id: tagId,
        onClick: () => this.removeFilterTags([tag]),
      },
      value,
      filterType,
    };

    return tag;
  }

  private removeFilterTags = (
    tags: ISelfTapeRequestNotificationFilterTag[]
  ) => {
    const tagsCollection = groupBy(tags, (tag) => tag.filterType);
    const iterator = (
      model: ISelfTapeRequestNotificationFilterControlModel,
      tagsByType: ISelfTapeRequestNotificationFilterTag[],
      filterType: FilterType
    ) =>
      (model[filterType] = this.removeFilterTypeTags(filterType, tagsByType));
    const accumulator = {
      ...this.props.filterValues,
      [FilterType.PageNumber]: 1,
    } as ISelfTapeRequestNotificationFilterControlModel;

    const updatedValues = transform(tagsCollection, iterator, accumulator);

    this.props.changeFilters(updatedValues);
  };

  private removeFilterTypeTags = (
    filterType: FilterType,
    filterTags: ISelfTapeRequestNotificationFilterTag[]
  ) => {
    const {
      filterControlType,
      drawerFilterControlType,
      checkboxOptions,
      defaultValue,
    } = this.props.filterConfig[filterType];

    if (drawerFilterControlType === FilterControlType.Checkbox) {
      const checkboxGroupValue = this.props.filterValues[
        filterType
      ] as ICheckboxGroupValue;
      const filteredValues = mapCheckboxGroupValueToValues(
        checkboxGroupValue
      ).filter((value) => filterTags.every((tag) => value !== tag.value));

      return mapOptionsToCheckboxGroupValue(checkboxOptions, filteredValues);
    }

    if (
      filterControlType === FilterControlType.DatePicker ||
      drawerFilterControlType === FilterControlType.DatePicker
    ) {
      return defaultValue;
    }

    return undefined;
  };

  private getDropdownCheckboxGroupConfig(filterType: FilterType) {
    return {
      name: filterType,
      options: this.props.filterConfig[filterType].checkboxOptions,
    } as IDropdownCheckboxProps["checkboxGroupConfig"];
  }

  public static getTagId = (filterType: FilterType, optionId: string = "") =>
    `${filterType}_${optionId}`;

  public static getTagName = (translation: string, value: string) =>
    `${translation}: ${value}`;
}

export default withLaunchDarklyConsumerHOC()(
  SelfTapeRequestNotificationFilterControl
) as React.ComponentType<ISelfTapeRequestNotificationFilterControlComposedProps>;
